import { Rule } from 'antd/es/form';
import { every, find, get, isEqual, reverse, uniqBy } from 'lodash';
import dayjs from 'dayjs';

import { IUser } from 'types/corpus-upgraded';
import { BlindTask } from 'types/review';

export const checkFieldName = (_rule: Rule, value: string): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (!/^[a-z_]+$/.test(value)) {
      reject('字段名必须全部采用小写英文单词，单词之间用”_”隔开');
    } else if (!value || value.length === 0) {
      reject('字段名不能为空');
    } else if (value.length > 30) {
      reject('字段名不能超过30个字符');
    } else {
      resolve();
    }
  });
};

export const getPaginationProps = (props: any) => ({
  showQuickJumper: true,
  showSizeChanger: true,
  showTotal: (total: number) => `总数：${total} 条`,
  ...props,
});

export const handleTableChange = (callback: Function, pagination: any, filters: any, sorter: any, extra: any) => {
  const obj: any = {
    page: pagination.current,
    page_size: pagination.pageSize,
    ...extra,
  };

  // 删除分页方法扩展属性值 currentDataSource + action
  delete obj.currentDataSource;
  delete obj.action;

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      obj[key] = value.join(',');
    } else {
      obj[key] = undefined;
    }
  });
  if (sorter && sorter.order) {
    obj.sorter = `${sorter.field},${sorter.order}`;
  } else {
    obj.sorter = undefined;
  }
  callback(obj);
};

export const areKeysUnique = (fields: any[]) => {
  if (!fields.length) {
    return [];
  }
  const seenKeys = new Set<string>();
  const repeatArr = [];
  for (const field of fields) {
    if (seenKeys.has(field.key)) {
      repeatArr.push(field.key);
    }
    seenKeys.add(field.key);
  }
  return repeatArr;
};

// 提取当前数据源中的唯一值
const getUniqueValuesWithCounts = (data: any[], key: string, keyName: string) => {
  const uniqueValues = new Map();
  data.forEach((record: any) => {
    const ext = record[keyName] ? JSON.parse(record[keyName]) : {};
    if (ext?.[key]) {
      const value = ext[key];
      if (uniqueValues.has(value)) {
        uniqueValues.set(value, uniqueValues.get(value) + 1);
      } else {
        uniqueValues.set(value, 1);
      }
    }
  });
  return uniqueValues;
};

// 生成用户过滤器
export const getUserFiltersWithCounts = (userList: IUser[], data: any[], key: string, keyName: string) => {
  const uniqueUserIdsWithCounts = getUniqueValuesWithCounts(data, key, keyName);
  return Array.from(uniqueUserIdsWithCounts.entries()).map(([userId, count]) => {
    const user = userList.find((u) => u.user_id === userId);
    return {
      key: userId,
      value: userId,
      text: `${user?.user_name || '暂无'}`,
      count,
    };
  });
};
interface FilterMap {
  [key: string]: string;
}
// 生成选项过滤器
export const getSelectFiltersWithCounts = (data: any[], filterMap: FilterMap, curKey: string) => {
  const optionCounts: Record<string, number> = Object.keys(filterMap).reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {} as Record<string, number>);

  // 统计选项出现次数
  data.forEach((record) => {
    const val = record[curKey];
    if (Array.isArray(val)) {
      val.forEach((v) => {
        if (optionCounts[v] !== undefined) {
          optionCounts[v] += 1;
        }
      });
    } else {
      if (optionCounts[val] !== undefined) {
        optionCounts[val] += 1;
      }
    }
  });

  return Object.keys(filterMap).map((optionKey) => ({
    text: `${filterMap[optionKey]}`,
    value: optionKey,
    key: optionKey,
    count: optionCounts[optionKey],
  }));
};

export const areModelsConsistent = (tasks: BlindTask[]) => {
  if (tasks.length === 0) return true;

  // 取第一个任务的模型作为参考
  const referenceTask = tasks[0];
  const referenceModels = [referenceTask.model_a, referenceTask.model_b].sort();

  return every(tasks, (task) => {
    const currentModels = [task.model_a, task.model_b].sort();
    return isEqual(currentModels, referenceModels);
  });
};

// 判断是否所有字段非空
export const isAllKeysNotEmpty = (formulaRule: [], record: any) => {
  return every(formulaRule, (key) => {
    const val = get(record, key);
    return val !== null && val !== undefined && val !== '';
  });
};

// 校验fieldName是否有重复值
export const validateUniqueValues = (array: any, fieldName: string) => {
  const uniqueItems = uniqBy(array, fieldName);
  return uniqueItems.length === array.length;
};

export const safeParseJson = (data: string) => {
  try {
    const params = JSON.parse(data);
    return params;
  } catch (error) {
    console.error('解析JSON失败:', error);
    return {};
  }
};

// 分割日期和时间
export const splitDateTime = (s: string) => {
  if (!s) return { date: '-', time: '' };
  const [date, ...rest] = s.split(' ');
  return { date, time: rest.join(' ') };
};

// 将时间字符串转换为秒级时间戳
export const isoToSecTimestamp = (isoStr: string): number => {
  return Math.floor(new Date(isoStr).getTime() / 1000);
};
// 将秒级时间戳转换为 "YYYY-MM-DD HH:mm:ss" 格式的时间字符串（本地时区）
export const secTimestampToDateStr = (sec: number): string => dayjs.unix(sec).format('YYYY-MM-DD HH:mm:ss');
